import React, { useEffect } from "react";

import { Route, Routes, Navigate } from "react-router-dom";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  environmentSelector,
  environmentLoadingSelector,
  loadEnvironment
} from "Reducers/environment";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { gitProjectSelector } from "Reducers/project/project";
import { loadProjectSetting } from "Reducers/projectSettings";
import BillingPage from "src/organization/pages/billing/Page";
import ProjectBilling from "src/organization/pages/billing/projectBilling/ProjectBilling";
import ConfigureAutoscalingPage from "src/organization/pages/project/environment/ConfigureAutoscaling";
import ConfigureResourcesPage from "src/organization/pages/project/environment/ConfigureResourcesPage";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import PluginApp from "./app/Page";
import ProjectInvitationWaitingScreen from "./containers/ProjectInvitationWaitingScreen";
import EnvironmentRoutes from "./environment/Routes";
import ManageBackupSchedulePage from "./ManageBackupSchedulePage";
import { NoEnvironmentRedirect } from "./NoEnvironmentRedirect";
import OverviewPage from "./overview/Page";
import ProjectPage from "./Page";
import IntegrationSetup from "./settings/integrations/Setup/Page";
import SettingsRoutes from "./settings/Routes";

const OrganizationProjectRoutes = () => {
  const dispatch = useAppDispatch();

  const { organizationId, projectId, environmentId } = useDecodedParams();

  const usageBasedBillingEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );
  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector
  );
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const environment = useSelectorWithUrlParams(environmentSelector, {
    environmentId: environmentId ?? ""
  });

  useEffect(() => {
    if (project?.id) dispatch(loadProjectSetting({ projectId: project.id }));
  }, [dispatch, project?.id]);

  useEffect(() => {
    if (!environment && !isEnvironmentLoading) {
      loadEnvironment({ organizationId, projectId, environmentId });
    }
  }, [
    dispatch,
    environment,
    isEnvironmentLoading,
    organizationId,
    projectId,
    environmentId
  ]);

  return (
    <Routes>
      <Route
        path="-/settings/integrations/setup/*"
        element={
          <ProjectPage>
            <IntegrationSetup />
          </ProjectPage>
        }
      />

      <Route
        path="-/settings/*"
        element={
          <ProjectPage>
            <SettingsRoutes />
          </ProjectPage>
        }
      />

      <Route path="-/wait" element={<ProjectInvitationWaitingScreen />} />

      <Route
        path="-/app/:appId"
        element={
          <ProjectPage>
            <PluginApp
              route="/:organizationId/:projectId/"
              type="tab"
              organizationId={organizationId}
              projectId={projectId}
            />
          </ProjectPage>
        }
      />

      <Route
        path="-/log/:activityId"
        element={
          <ProjectPage>
            <OverviewPage />
          </ProjectPage>
        }
      />

      <Route
        path="subscriptions/:subscriptionId/-/billing"
        element={
          usageBasedBillingEnabled ? (
            <BillingPage>
              <ProjectBilling />
            </BillingPage>
          ) : (
            <Navigate to="/" />
          )
        }
      />

      <Route
        path=":environmentId/resources"
        element={
          <ProjectPage>
            <NoEnvironmentRedirect>
              <ConfigureResourcesPage />
            </NoEnvironmentRedirect>
          </ProjectPage>
        }
      />

      <Route
        path=":environmentId/autoscaling"
        element={
          <ProjectPage>
            <NoEnvironmentRedirect>
              <ConfigureAutoscalingPage />
            </NoEnvironmentRedirect>
          </ProjectPage>
        }
      />

      <Route
        path="backup-schedules/:environmentType"
        element={
          <ProjectPage>
            <ManageBackupSchedulePage />
          </ProjectPage>
        }
      />

      <Route
        path=":environmentId/*"
        element={
          <ProjectPage>
            <NoEnvironmentRedirect>
              <EnvironmentRoutes />
            </NoEnvironmentRedirect>
          </ProjectPage>
        }
      />

      <Route
        path=""
        element={
          <ProjectPage>
            <OverviewPage />
          </ProjectPage>
        }
      />
    </Routes>
  );
};

export default OrganizationProjectRoutes;
